import { z } from "zod";
/**
 * Assistant reply streamed (as the entire turn) when web AI chat is disabled for
 * an org — `organization_faq_config.web_ai_enabled = false`, or no config row at
 * all. The backend chat stream returns only this; the web widget also stops
 * running local FAQ matching for these orgs, so the whole experience becomes
 * just this notice. Cheer Sagar (`web_ai_enabled = true`) is unaffected.
 */
export const WEB_AI_DISABLED_MESSAGE = "The AI chat agent will be re-enabled once the service resumes and the dashboard is updated with latest data.";
/** Request body for POST /agents/chat */
export const chatRequestSchema = z.object({
    message: z.string().min(1),
    thread_id: z.string().min(1).optional(),
    tool_names: z.array(z.string()).optional(),
    state: z
        .object({
        dashboard_id: z.string().optional(),
        widget_id: z.string().optional(),
        start_date: z.string().optional(),
        end_date: z.string().optional(),
    })
        .optional(),
});
/**
 * Payload of the SSE `error` event emitted by the chat stream when something
 * goes wrong mid-turn (LLM timeout, recursion limit, etc.). The agent's
 * partial output up to that point has already been saved via the LangGraph
 * checkpointer, so the UI can render a friendly chip with a Retry button
 * and re-send the same prompt to the same thread.
 */
export const chatStreamErrorEventSchema = z.object({
    error: z.string(),
    code: z.enum(["timeout", "recursion_limit", "internal"]).optional(),
    message: z.string(),
    recoverable: z.boolean().optional(),
    details: z.string().optional(),
});
export const threadSchema = z.object({
    id: z.string(),
    title: z.string().optional().nullable(),
    // Flow mode has been removed; "flow" is retained here only so legacy
    // conversation_threads rows (persisted before the removal) still parse.
    mode: z.enum(["qa", "flow"]).optional(),
    createdAt: z.coerce.date(),
    updatedAt: z.coerce.date(),
});
export const threadHistoryMessageSchema = z.discriminatedUnion("type", [
    z.object({
        id: z.string(),
        type: z.literal("human"),
        role: z.literal("user"),
        content: z.string(),
        timestamp: z.string(),
    }),
    z.object({
        id: z.string(),
        type: z.literal("ai"),
        role: z.literal("assistant"),
        content: z.string(),
        timestamp: z.string(),
    }),
    z.object({
        id: z.string(),
        type: z.literal("tool_status"),
        role: z.literal("tool"),
        /** Internal tool name (e.g. "execute_tally_sql"). */
        toolName: z.string(),
        /** Human-readable status label (e.g. "Crunching data..."). */
        statusText: z.string(),
        /** Always "done" — historical entries are restored as completed. */
        status: z.literal("done"),
        /** JSON-serialized tool input (args). Shown in the hover popover. */
        input: z.string().optional(),
        /** Tool result preview. Shown in the hover popover. */
        output: z.string().optional(),
        timestamp: z.string(),
    }),
    z.object({
        id: z.string(),
        type: z.literal("display_table"),
        role: z.literal("tool"),
        data: z.record(z.string(), z.unknown()),
        timestamp: z.string(),
    }),
    z.object({
        id: z.string(),
        type: z.literal("display_chart"),
        role: z.literal("tool"),
        data: z.record(z.string(), z.unknown()),
        timestamp: z.string(),
    }),
    z.object({
        id: z.string(),
        type: z.literal("display_google_sheet"),
        role: z.literal("tool"),
        data: z.record(z.string(), z.unknown()),
        timestamp: z.string(),
    }),
]);
